(function () {
  'use strict';

  var PAGE_SIZE = 36;
  var localColoursPromise = null;
  var MODAL_ATTR = 'data-bm-colour-modal';

  function cleanText(value) {
    return String(value || '').replace(/&reg;/g, '').trim();
  }

  function normalise(value) {
    return cleanText(value)
      .toLowerCase()
      .replace(/[’‘]/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }

  function normaliseHex(value) {
    var hex = String(value || '').replace('#', '').trim();
    return /^[0-9a-fA-F]{6}$/.test(hex) ? '#' + hex.toUpperCase() : '';
  }

  function readConfig() {
    var node = document.querySelector('[data-bm-local-config]');
    if (!node) return null;

    try {
      var config = JSON.parse(node.textContent || '{}');
      return Array.isArray(config.shards) ? config : null;
    } catch (error) {
      return null;
    }
  }

  function loadLocalColours() {
    if (localColoursPromise) return localColoursPromise;

    localColoursPromise = Promise.resolve().then(function () {
      var config = readConfig();
      if (!config) throw new Error('Benjamin Moore local colour config missing.');

      return Promise.all(config.shards.map(function (url) {
        return fetch(url, { credentials: 'same-origin', cache: 'force-cache' }).then(function (response) {
          if (!response.ok) throw new Error('Could not load Benjamin Moore local colour data.');
          return response.json();
        });
      })).then(function (shards) {
        var colours = [];

        shards.forEach(function (rows) {
          if (!Array.isArray(rows)) return;
          rows.forEach(function (row) {
            if (!Array.isArray(row) || row.length < 5) return;

            var name = cleanText(row[0]);
            var number = cleanText(row[1]);
            if (!name || !number) return;

            colours.push({
              name: name,
              number: number,
              hex: normaliseHex(row[2]),
              collection: config.collections && config.collections[row[3]] ? config.collections[row[3]] : '',
              family: config.families && config.families[row[4]] ? config.families[row[4]] : ''
            });
          });
        });

        if (config.count && colours.length !== Number(config.count)) {
          throw new Error('Benjamin Moore local colour database is incomplete.');
        }

        return { colours: colours, config: config };
      });
    });

    return localColoursPromise;
  }

  function setCoreStatus(ui, message, isError) {
    var status = ui.querySelector('[data-bm-status]');
    if (!status) return;
    status.textContent = message || '';
    status.hidden = !message;
    status.classList.toggle('bm-colour-status--error', !!isError);
  }

  function metaLine(colour) {
    var bits = [colour.number];
    if (colour.family) bits.push(colour.family);
    if (colour.collection) bits.push(colour.collection);
    return bits.join(' · ');
  }

  function chooseThroughCoreSelector(ui, colour, done, attempt) {
    var search = ui.querySelector('[data-bm-search]');
    var tries = attempt || 0;
    if (!search) {
      if (done) done(false);
      return;
    }

    search.value = colour.number;
    search.dispatchEvent(new Event('input', { bubbles: true }));

    var exactButton = null;
    ui.querySelectorAll('[data-bm-colour-result]').forEach(function (button) {
      if (exactButton) return;
      var meta = button.querySelector('.bm-colour-result-copy span');
      var code = meta ? meta.textContent.split('·')[0].trim() : '';
      if (normalise(code) === normalise(colour.number)) exactButton = button;
    });

    if (exactButton) {
      exactButton.click();
      if (done) done(true);
      return;
    }

    if (tries < 20) {
      window.setTimeout(function () {
        chooseThroughCoreSelector(ui, colour, done, tries + 1);
      }, 100);
      return;
    }

    setCoreStatus(ui, 'This colour could not be selected. Please try searching by its code.', true);
    if (done) done(false);
  }

  function createColourCard(colour, onSelect) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'bm-modal-colour-card';
    button.setAttribute('data-bm-modal-colour-card', '');
    button.setAttribute('aria-label', 'Select ' + colour.name + ' ' + colour.number);
    button.innerHTML =
      '<span class="bm-modal-colour-card__swatch" aria-hidden="true"></span>' +
      '<span class="bm-modal-colour-card__copy">' +
        '<strong></strong>' +
        '<span class="bm-modal-colour-card__code"></span>' +
        '<span class="bm-modal-colour-card__meta"></span>' +
      '</span>';

    var swatch = button.querySelector('.bm-modal-colour-card__swatch');
    swatch.style.backgroundColor = colour.hex || '#ffffff';
    swatch.classList.toggle('bm-colour-swatch--unknown', !colour.hex);
    button.querySelector('strong').textContent = colour.name;
    button.querySelector('.bm-modal-colour-card__code').textContent = colour.number;
    button.querySelector('.bm-modal-colour-card__meta').textContent = [colour.family, colour.collection].filter(Boolean).join(' · ');
    button.addEventListener('click', function () { onSelect(colour); });

    return button;
  }

  function buildModal(ui, trigger, payload) {
    var oldModal = document.querySelector('[' + MODAL_ATTR + ']');
    if (oldModal) oldModal.remove();

    var colours = payload.colours;
    var config = payload.config;
    var activeFamily = '';
    var visibleCount = PAGE_SIZE;
    var lastFocused = trigger;

    var modal = document.createElement('div');
    modal.className = 'bm-colour-modal';
    modal.setAttribute(MODAL_ATTR, '');
    modal.hidden = true;
    modal.innerHTML =
      '<div class="bm-colour-modal__backdrop" data-bm-modal-close></div>' +
      '<div class="bm-colour-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="bm-colour-modal-title">' +
        '<div class="bm-colour-modal__header">' +
          '<div>' +
            '<span class="bm-colour-modal__eyebrow">Benjamin Moore Colour Browser</span>' +
            '<h2 id="bm-colour-modal-title">Browse all 3,870 colours</h2>' +
            '<p>Search, filter and compare colours, then select one to return to your paint.</p>' +
          '</div>' +
          '<button type="button" class="bm-colour-modal__close" data-bm-modal-close aria-label="Close colour browser">&times;</button>' +
        '</div>' +
        '<div class="bm-colour-modal__controls">' +
          '<label class="bm-modal-search">' +
            '<span>Search by colour name or code</span>' +
            '<div class="bm-modal-search__input-wrap">' +
              '<span aria-hidden="true">&#128269;</span>' +
              '<input type="search" autocomplete="off" spellcheck="false" placeholder="e.g. White Dove or OC-17" data-bm-modal-search>' +
            '</div>' +
          '</label>' +
          '<label class="bm-modal-collection">' +
            '<span>Collection</span>' +
            '<select data-bm-modal-collection><option value="">All collections</option></select>' +
          '</label>' +
          '<div class="bm-modal-family">' +
            '<span>Colour family</span>' +
            '<div class="bm-modal-family__chips" data-bm-modal-families></div>' +
          '</div>' +
          '<button type="button" class="bm-modal-clear" data-bm-modal-clear hidden>Clear filters</button>' +
        '</div>' +
        '<div class="bm-colour-modal__body">' +
          '<div class="bm-colour-modal__status" data-bm-modal-status aria-live="polite"></div>' +
          '<div class="bm-colour-modal__grid" data-bm-modal-grid></div>' +
          '<button type="button" class="bm-colour-modal__more" data-bm-modal-more>Show more colours</button>' +
        '</div>' +
      '</div>';

    document.body.appendChild(modal);

    var dialog = modal.querySelector('.bm-colour-modal__dialog');
    var search = modal.querySelector('[data-bm-modal-search]');
    var collectionSelect = modal.querySelector('[data-bm-modal-collection]');
    var familyWrap = modal.querySelector('[data-bm-modal-families]');
    var clearButton = modal.querySelector('[data-bm-modal-clear]');
    var grid = modal.querySelector('[data-bm-modal-grid]');
    var status = modal.querySelector('[data-bm-modal-status]');
    var showMore = modal.querySelector('[data-bm-modal-more]');

    (config.collections || []).forEach(function (collection) {
      var option = document.createElement('option');
      option.value = collection;
      option.textContent = collection;
      collectionSelect.appendChild(option);
    });

    (config.families || []).forEach(function (family) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'bm-modal-family__chip';
      chip.textContent = family;
      chip.setAttribute('data-family', family);
      chip.setAttribute('aria-pressed', 'false');
      chip.addEventListener('click', function () {
        activeFamily = activeFamily === family ? '' : family;
        familyWrap.querySelectorAll('[data-family]').forEach(function (item) {
          var active = item.getAttribute('data-family') === activeFamily;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        visibleCount = PAGE_SIZE;
        render();
      });
      familyWrap.appendChild(chip);
    });

    function filteredColours() {
      var query = normalise(search.value);
      var collectionValue = collectionSelect.value;

      return colours.filter(function (colour) {
        if (activeFamily && colour.family !== activeFamily) return false;
        if (collectionValue && colour.collection !== collectionValue) return false;
        if (query.length >= 2 && normalise(colour.name).indexOf(query) === -1 && normalise(colour.number).indexOf(query) === -1) return false;
        return true;
      });
    }

    function render() {
      var matches = filteredColours();
      var shown = matches.slice(0, visibleCount);
      grid.innerHTML = '';

      shown.forEach(function (colour) {
        grid.appendChild(createColourCard(colour, function (chosen) {
          chooseThroughCoreSelector(ui, chosen, function (success) {
            if (success) closeModal();
          });
        }));
      });

      if (!matches.length) {
        status.textContent = 'No Benjamin Moore colours match these filters. Try another search, family or collection.';
      } else {
        status.textContent = 'Showing ' + shown.length + ' of ' + matches.length + (matches.length === 1 ? ' colour.' : ' colours.');
      }

      showMore.hidden = !matches.length || shown.length >= matches.length;
      clearButton.hidden = !(search.value || activeFamily || collectionSelect.value);
    }

    function openModal() {
      lastFocused = document.activeElement || trigger;
      modal.hidden = false;
      document.documentElement.classList.add('bm-colour-modal-open');
      document.body.classList.add('bm-colour-modal-open');
      visibleCount = PAGE_SIZE;
      render();
      window.setTimeout(function () { search.focus(); }, 0);
    }

    function closeModal() {
      modal.hidden = true;
      document.documentElement.classList.remove('bm-colour-modal-open');
      document.body.classList.remove('bm-colour-modal-open');
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function trapFocus(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key !== 'Tab' || modal.hidden) return;
      var focusable = dialog.querySelectorAll('button:not([disabled]):not([hidden]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    modal.querySelectorAll('[data-bm-modal-close]').forEach(function (button) {
      button.addEventListener('click', closeModal);
    });

    search.addEventListener('input', function () {
      visibleCount = PAGE_SIZE;
      render();
    });

    collectionSelect.addEventListener('change', function () {
      visibleCount = PAGE_SIZE;
      render();
    });

    clearButton.addEventListener('click', function () {
      search.value = '';
      collectionSelect.value = '';
      activeFamily = '';
      familyWrap.querySelectorAll('[data-family]').forEach(function (item) {
        item.classList.remove('is-active');
        item.setAttribute('aria-pressed', 'false');
      });
      visibleCount = PAGE_SIZE;
      render();
      search.focus();
    });

    showMore.addEventListener('click', function () {
      visibleCount += PAGE_SIZE;
      render();
    });

    modal.addEventListener('keydown', trapFocus);
    trigger.addEventListener('click', openModal);

    return modal;
  }

  function enhanceProductSelector(ui) {
    if (!ui || ui.hasAttribute('data-bm-product-browse-ready')) return;
    ui.setAttribute('data-bm-product-browse-ready', '');

    var searchRow = ui.querySelector('.bm-colour-search-row');
    var selected = ui.querySelector('[data-bm-selected]');
    if (!searchRow || !selected) return;

    var intro = document.createElement('div');
    intro.className = 'bm-product-colour-intro';
    intro.innerHTML =
      '<strong>Choose your Benjamin Moore colour</strong>' +
      '<span>Search by colour name or code, or open the full colour browser.</span>';
    ui.insertBefore(intro, searchRow);

    var browseAction = document.createElement('div');
    browseAction.className = 'bm-product-browse-action';
    browseAction.innerHTML =
      '<button type="button" class="bm-product-browse-trigger" data-bm-open-colour-browser>' +
        '<span class="bm-product-browse-trigger__icon" aria-hidden="true">▦</span>' +
        '<span><strong>Browse all Benjamin Moore colours</strong><small>Explore 3,870 colours by family and collection</small></span>' +
        '<span class="bm-product-browse-trigger__arrow" aria-hidden="true">→</span>' +
      '</button>';
    searchRow.insertAdjacentElement('afterend', browseAction);

    var trigger = browseAction.querySelector('[data-bm-open-colour-browser]');
    trigger.disabled = true;
    trigger.setAttribute('aria-busy', 'true');

    loadLocalColours().then(function (payload) {
      trigger.disabled = false;
      trigger.removeAttribute('aria-busy');
      buildModal(ui, trigger, payload);
    }).catch(function (error) {
      console.error('Benjamin Moore colour browser error:', error);
      trigger.disabled = true;
      trigger.removeAttribute('aria-busy');
      trigger.querySelector('small').textContent = 'Colour browser temporarily unavailable — use search above';
    });
  }

  function boot() {
    document.querySelectorAll('.bm-colour-selector--product').forEach(enhanceProductSelector);
  }

  boot();
  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', function () {
    window.setTimeout(boot, 0);
  });
  document.addEventListener('variant:changed', function () {
    window.setTimeout(boot, 100);
  });
})();
